import { Can } from '@/components/can/Can';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { DeleteIcon, EditIcon } from '@/icons';
import { CommissionSlabs } from '@/services/slabs/slabs.types';
import type { ColumnDef } from '@tanstack/react-table';
import { ReactNode } from 'react';
import { ManageCommissionSlabContainer } from '../../ManageCommissionSlabsContainer';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

export interface deletePayload {
  categoryDetailId: string;
  categoryId: string;
  status: string;
}

export function getColumns(
  onOpen: OnOpenProps,
  mutate: (payload: deletePayload) => Promise<unknown>,
  openConfirmation: OpenConfirmationProps,
  t: (key: string) => string,
  adminCommissionTypeId?: string,
  categoryId?: string,
): ColumnDef<CommissionSlabs>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => (
        <DataTableColumnHeader className="whitespace-nowrap" column={column} title={t('common.page.tableColumn.sn')} />
      ),
      cell: ({ row, table }) => (
        <div className="font-medium truncate max-w-[10rem]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'fromAmount',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.commissions.manageSlabsModal.fromAmount')}
        />
      ),
      cell: ({ row }) => <div className="font-medium truncate max-w-[20rem]">{row.getValue('fromAmount')}</div>,
      meta: {
        label: 'From Amount (円)',
        align: 'right',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'toAmount',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.commissions.manageSlabsModal.toAmount')}
        />
      ),
      cell: ({ row }) => <div className="font-medium truncate max-w-[20rem]">{row.getValue('toAmount')}</div>,
      meta: {
        label: 'To Amount (円)',
        align: 'right',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'commissionPercentageType',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.commissions.manageSlabsModal.categoryType')}
        />
      ),
      cell: ({ row }) => {
        return row.getValue('commissionPercentageType') === 'F' ? (
          <div className="font-medium truncate max-w-[20rem] text-link">
            {t('configurationManagement.commissions.table.title.flat')}
          </div>
        ) : (
          <div className="font-medium truncate max-w-[20rem] text-link">
            {t('configurationManagement.commissions.table.title.percentage')}
          </div>
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: 'commissionValue',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.commissions.manageSlabsModal.value')}
        />
      ),
      cell: ({ row }) => <div>{row.getValue('commissionValue')}</div>,
      meta: {
        label: 'Value',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'minCommissionValue',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.commissions.manageSlabsModal.minValue')}
        />
      ),
      cell: ({ row }) => <div>{row.getValue('minCommissionValue')}</div>,
      meta: {
        label: 'Min Value',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'maxCommissionValue',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.commissions.manageSlabsModal.maxValue')}
        />
      ),
      cell: ({ row }) => <div>{row.getValue('maxCommissionValue')}</div>,
      meta: {
        label: 'Max Value',
      },
      enableHiding: false,
    },
    {
      id: 'actions',
      accessorKey: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex gap-4 items-center">
          <Can rule={{ allOf: [PERMS.commission.detail.manage] }}>
            <EditIcon
              className="text-foreground-subtle size-4 cursor-pointer"
              onClick={() =>
                onOpen({
                  title: 'Manage Commission',
                  okText: <CrumbTitle id="common.page.button.submit" />,
                  size: 'lg',
                  modalTitle: 'configurationManagement.commissions.manageSlabsModal.title',
                  component: (
                    <ManageCommissionSlabContainer
                      categoryId={categoryId}
                      categoryDetailId={row.original.categoryDetailId}
                      adminCommissionTypeId={adminCommissionTypeId}
                    />
                  ),
                })
              }
            />
          </Can>
          <Can rule={{ allOf: [PERMS.commission.detail.delete] }}>
            <DeleteIcon
              className="text-destructive-text size-4 cursor-pointer"
              onClick={() => {
                openConfirmation({
                  title: t('common.page.modal.confirm'),
                  description: t('configurationManagement.commissions.deleteSlabModal.message'),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.submit'),
                  onAction: async () => {
                    await mutate({
                      categoryDetailId: row.original.categoryDetailId,
                      categoryId: row.original.categoryId,
                      status: 'D',
                    });
                  },
                  onCancel: () => {},
                  size: 'lg',
                  variant: 'destructive',
                });
              }}
            />
          </Can>
        </div>
      ),
      size: 40,
    },
  ];
}
